import React from "react";
import { Box, Typography, Divider, Alert, Chip } from "@mui/material";
import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import StatusChip from "../common/StatusChip";
import AppCircularProgress from "../common/AppCircularProgress";
import { EnrollmentResponseDto } from "../../shared/dtos/enrollment/EnrollmentResponseDto";
import { formatDateForDisplay } from "../../helpers/dateHelpers";

interface ViewEnrollmentModalProps {
  open: boolean;
  onClose: () => void;
  enrollment: EnrollmentResponseDto | null;
  loading: boolean;
  error?: string | null;
}

const ViewEnrollmentModal: React.FC<ViewEnrollmentModalProps> = ({
  open,
  onClose,
  enrollment,
  loading,
  error = null,
}) => {
  const renderContent = (): React.ReactNode => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 5,
          }}
        >
          <AppCircularProgress />
          <Typography sx={{ ml: 2, color: "text.secondary" }}>
            Loading Enrollment Details...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!enrollment) {
      return (
        <Typography sx={{ my: 2, color: "text.secondary" }}>
          Enrollment data not available.
        </Typography>
      );
    }

    return (
      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            alignItems: "center",
          }}
        >
          <DetailItem
            label="Student Name"
            value={enrollment.studentName || "N/A"}
          />
          <DetailItem
            label="Admission No."
            value={
              <Chip
                size="small"
                label={enrollment.admissionNumber || "N/A"}
                variant="outlined"
              />
            }
          />
          <DetailItem
            label="Classroom"
            value={enrollment.classroomName || "N/A"}
          />
          <DetailItem
            label="Academic Year"
            value={enrollment.academicYearName || "N/A"}
          />
          <DetailItem label="Branch" value={enrollment.branchName || "N/A"} />
          <Box>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 0.5 }}
            >
              Status
            </Typography>
            <StatusChip status={enrollment.status} />
          </Box>

          <DetailItem
            label="Enrollment Date"
            value={formatDateForDisplay(enrollment.enrollmentDate)}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem
            label="Created At"
            value={new Date(enrollment.createdAt).toLocaleString()}
          />
          <DetailItem
            label="Last Updated"
            value={new Date(enrollment.updatedAt).toLocaleString()}
          />
        </Box>
      </Box>
    );
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Enrollment Details"
      width="md"
    >
      {renderContent()}
    </AppModal>
  );
};

export default ViewEnrollmentModal;
